import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import FormModalLayout from '../../components/Common/FormModalLayout';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import {
  complaintService,
  fiscalizacaoService,
  institutionService,
  identityService,
  processService,
  licenseService,
  routingService,
} from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import { complaintDisplayName, personDisplayName, processoDisplayName, shortId } from '../../utils/lookupLabels';
import MapLocationPicker from '../../components/Map/MapLocationPicker';
import { userFacingError } from '../../i18n/userFacingError';
import SlaBadge from '../../components/Common/SlaBadge';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? [];
}

const CHECKLISTS = {
  LP: [
    'Localização conferida no terreno',
    'Estudo ambiental apresentado',
    'Uso e ocupação do solo compatível',
    'Área de preservação observada',
  ],
  LI: [
    'Conformidade com a LP',
    'Obras conforme projeto',
    'Controle de efluentes e emissões',
    'Sinalização e segurança de obra',
  ],
  LO: [
    'Operação conforme projeto aprovado',
    'Monitoramento ambiental em dia',
    'Condicionantes cumpridas',
    'Plano de emergência disponível',
  ],
};

const emptyVistoria = {
  complaint_id: '',
  license_id: '',
  processo_id: '',
  license_tipo: 'LP',
  checks: {},
  latitude: '',
  longitude: '',
  notes: '',
  photo_base64: '',
  status: 'aberta',
  servidor_id: '',
};
const emptyAuto = {
  complaint_id: '',
  vistoria_id: '',
  processo_id: '',
  tipo: 'infracao',
  numero: '',
  descricao: '',
  status: 'lavrado',
  prazo_defesa: '',
  area_geojson: '',
};

const VISTORIA_STATUS = [
  { value: 'aberta', label: 'Aberta' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluida', label: 'Concluída' },
];
const AUTO_STATUS = [
  { value: 'lavrado', label: 'Lavrado' },
  { value: 'notificado', label: 'Notificado' },
  { value: 'aguardando_defesa', label: 'Aguardando defesa' },
  { value: 'defesa_apresentada', label: 'Defesa apresentada' },
  { value: 'julgado', label: 'Julgado' },
  { value: 'pago', label: 'Pago' },
  { value: 'anulado', label: 'Anulado' },
];

function serializeChecklist(tipo, checks) {
  const items = CHECKLISTS[tipo] || [];
  return items.map((label) => `${checks[label] ? '[x]' : '[ ]'} ${label}`).join('\n');
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const Fiscalizacao = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('vistorias');
  const [showVistoria, setShowVistoria] = useState(false);
  const [showAuto, setShowAuto] = useState(false);
  const [vistoria, setVistoria] = useState(emptyVistoria);
  const [editingVistoriaId, setEditingVistoriaId] = useState(null);
  const [auto, setAuto] = useState(emptyAuto);
  const [julgar, setJulgar] = useState({ auto: null, decisao: 'deferida', decisao_texto: '' });
  const [route, setRoute] = useState(null);

  const { data: vistRaw, isLoading, error: vistError } = useQuery(['vistorias'], () =>
    fetchAllPages((params) => fiscalizacaoService.listVistorias(params))
  );
  const { data: autoRaw, error: autoError } = useQuery(['autos'], () =>
    fetchAllPages((params) => fiscalizacaoService.listAutos(params))
  );
  const { data: servidoresRaw = [] } = useQuery(['fiscal-servidores'], () =>
    fetchAllPages((params) => institutionService.getServidores(params))
  );
  const { data: personsRaw = [] } = useQuery(['fiscal-persons'], () =>
    fetchAllPages((params) => identityService.getPersons(params))
  );
  const { data: denRaw } = useQuery(['complaints'], () =>
    fetchAllPages((params) => complaintService.getComplaints(params))
  );
  const { data: procRaw } = useQuery(['processos-fiscal'], () =>
    fetchAllPages((params) => processService.getProcessos(params))
  );
  const { data: licRaw } = useQuery(['licencas-fiscal'], () =>
    fetchAllPages((params) => licenseService.getLicencas(params))
  );
  const vistorias = unwrap(vistRaw);
  const autos = unwrap(autoRaw);
  const complaints = unwrap(denRaw);
  const processos = unwrap(procRaw);
  const licencas = unwrap(licRaw);
  const complaintLabel = (id) =>
    complaintDisplayName(complaints.find((c) => c.id === id)) || (id ? shortId(id) : '—');
  const processoLabel = (id) =>
    processoDisplayName(processos.find((p) => p.id === id)) || (id ? shortId(id) : '—');
  const servidorLabel = (id) => {
    if (!id) return '—';
    const row = (servidoresRaw || []).find((s) => s.id === id);
    if (!row) return shortId(id);
    const person = (personsRaw || []).find((p) => p.id === row.person_id);
    return row.name || personDisplayName(person) || row.matricula || shortId(id);
  };
  const vistoriaStatusLabel = (value) =>
    VISTORIA_STATUS.find((o) => o.value === value)?.label || value || 'Aberta';
  const autoStatusLabel = (value) =>
    AUTO_STATUS.find((o) => o.value === value)?.label || value || 'Lavrado';

  const createVistoria = useMutation(
    ({ id: vistoriaId, ...payload }) =>
      vistoriaId
        ? fiscalizacaoService.updateVistoria(vistoriaId, payload)
        : fiscalizacaoService.createVistoria(payload),
    {
      onSuccess: () => {
        toast.success(editingVistoriaId ? 'Vistoria atualizada' : 'Vistoria registrada com foto e GPS');
        setShowVistoria(false);
        setEditingVistoriaId(null);
        setVistoria(emptyVistoria);
        queryClient.invalidateQueries(['vistorias']);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );
  const createAuto = useMutation((payload) => fiscalizacaoService.createAuto(payload), {
    onSuccess: () => {
      toast.success('Auto lavrado');
      setShowAuto(false);
      setAuto(emptyAuto);
      queryClient.invalidateQueries(['autos']);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const updateAuto = useMutation(
    ({ id: autoId, ...payload }) => fiscalizacaoService.updateAuto(autoId, payload),
    {
      onSuccess: () => {
        toast.success('Auto atualizado');
        queryClient.invalidateQueries(['autos']);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const captureGps = () => {
    if (!navigator.geolocation) {
      toast.error('GPS indisponível neste navegador');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setVistoria((f) => ({
          ...f,
          latitude: String(pos.coords.latitude),
          longitude: String(pos.coords.longitude),
        }));
        toast.success('Coordenadas capturadas');
      },
      () => toast.error('Não foi possível obter o GPS'),
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const onPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setVistoria((f) => ({ ...f, photo_base64: String(reader.result || '') }));
    reader.readAsDataURL(file);
  };

  const routeStops = [
    ...complaints
      .filter((c) => c.latitude != null && c.longitude != null)
      .map((c) => ({
        id: c.id,
        label: c.local || c.cidade || `Denúncia ${c.id.slice(0, 8)}`,
        latitude: Number(c.latitude),
        longitude: Number(c.longitude),
        kind: 'denuncia',
      })),
    ...vistorias
      .filter((v) => v.latitude != null && v.longitude != null)
      .map((v) => ({
        id: v.id,
        label: `${v.license_tipo || 'Vistoria'} · ${v.id.slice(0, 8)}`,
        latitude: Number(v.latitude),
        longitude: Number(v.longitude),
        kind: 'vistoria',
      })),
  ];

  const calcRoute = useMutation((stops) => routingService.orderTrip(stops), {
    onSuccess: (res) => {
      setRoute(res.data);
      toast.success('Rota calculada no OSRM compartilhado');
    },
    onError: (e) => toast.error(userFacingError(e, 'loadError')),
  });

  const downloadPdf = async (row) => {
    try {
      const { data } = await fiscalizacaoService.downloadAutoPdf(row.id);
      downloadBlob(data, `auto-${row.numero || row.id}.pdf`);
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  return (
    <FormPageShell>
      <PageHeader
        title="Fiscalização"
        description="Cadeia obrigatória: denúncia ou licença → vistoria → auto → processo. O auto sai em PDF."
        breadcrumbs={[
          { label: 'Início', to: getAppHomePath() },
          { label: 'Fiscalização' },
        ]}
      >
        <Can permission={P.COMPLAINT_ACCESS}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => {
              setEditingVistoriaId(null);
              setVistoria(emptyVistoria);
              setShowVistoria(true);
            }}
          >
            Nova vistoria
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setShowAuto(true)}>
            Novo auto
          </button>
        </Can>
      </PageHeader>
      <div className="mt-4 flex gap-2">
        <button type="button" className={tab === 'vistorias' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => setTab('vistorias')}>
          Vistorias
        </button>
        <button type="button" className={tab === 'autos' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => setTab('autos')}>
          Autos
        </button>
        <button type="button" className={tab === 'rota' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => setTab('rota')}>
          Rota
        </button>
      </div>
      {vistError || autoError ? <QueryErrorBanner error={vistError || autoError} className="mt-4" /> : null}
      <div className="data-list-card mt-6 overflow-hidden">
        {isLoading ? (
          <p className="px-6 py-8 text-gray-500">Carregando...</p>
        ) : tab === 'rota' ? (
          <div className="space-y-4 px-6 py-6">
            <p className="text-sm text-gray-600">
              Ordena denúncias e vistorias com GPS pela malha viária do OSRM compartilhado
              ({routeStops.length} ponto(s) com coordenada).
            </p>
            <button
              type="button"
              className="btn btn-primary"
              disabled={routeStops.length < 2 || calcRoute.isLoading}
              onClick={() => calcRoute.mutate(routeStops)}
            >
              Calcular rota de fiscalização
            </button>
            {routeStops.length < 2 ? (
              <p className="text-sm text-amber-800">Inclua GPS em pelo menos duas denúncias ou vistorias.</p>
            ) : null}
            {route?.stops?.length ? (
              <div>
                <p className="mb-2 text-sm text-gray-600">
                  {route.source === 'osrm' ? 'Ordem pela rua' : 'Ordem original'}
                  {route.distance_m != null ? ` · ${(route.distance_m / 1000).toFixed(1)} km` : ''}
                  {route.duration_s != null ? ` · ${Math.round(route.duration_s / 60)} min` : ''}
                </p>
                <ol className="list-decimal space-y-1 pl-5 text-sm">
                  {route.stops.map((stop) => (
                    <li key={`${stop.kind}-${stop.id}`}>
                      <span className="font-medium">{stop.label}</span>
                      <span className="ml-2 text-xs uppercase text-gray-500">{stop.kind}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        ) : tab === 'vistorias' ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Denúncia</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Processo</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Idade</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Servidor</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">GPS</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Foto</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Checklist</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {vistorias.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                    Nenhuma vistoria. Registre no campo com foto e GPS.
                  </td>
                </tr>
              ) : (
                vistorias.map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-4 text-sm">{complaintLabel(row.complaint_id)}</td>
                    <td className="px-6 py-4 text-sm">{processoLabel(row.processo_id)}</td>
                    <td className="px-6 py-4 text-sm">{row.license_tipo || '—'}</td>
                    <td className="px-6 py-4 text-sm">{vistoriaStatusLabel(row.status)}</td>
                    <td className="px-6 py-4 text-sm">
                      <SlaBadge
                        sla="sem_prazo"
                        idadeDias={
                          row.created_at
                            ? Math.max(
                                0,
                                Math.round(
                                  (Date.now() - new Date(row.created_at).getTime()) / 86400000
                                )
                              )
                            : null
                        }
                      />
                    </td>
                    <td className="px-6 py-4 text-sm">{servidorLabel(row.servidor_id)}</td>
                    <td className="px-6 py-4 text-sm">
                      {row.latitude != null && row.longitude != null
                        ? `${row.latitude}, ${row.longitude}`
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-wrap gap-2">
                        {row.photo_url ? (
                          <button
                            type="button"
                            className="text-primary-700"
                            onClick={async () => {
                              try {
                                const { data } = await fiscalizacaoService.downloadVistoriaFoto(row.id);
                                const url = URL.createObjectURL(data);
                                window.open(url, '_blank', 'noopener');
                              } catch (e) {
                                toast.error(userFacingError(e, 'loadError'));
                              }
                            }}
                          >
                            Ver foto
                          </button>
                        ) : (
                          <span>—</span>
                        )}
                        <button
                          type="button"
                          className="text-primary-700"
                          onClick={() => {
                            setEditingVistoriaId(row.id);
                            setVistoria({
                              ...emptyVistoria,
                              complaint_id: row.complaint_id || '',
                              license_id: row.license_id || '',
                              processo_id: row.processo_id || '',
                              license_tipo: row.license_tipo || emptyVistoria.license_tipo,
                              notes: row.notes || '',
                              latitude: row.latitude ?? '',
                              longitude: row.longitude ?? '',
                              status: row.status || 'aberta',
                              servidor_id: row.servidor_id || '',
                            });
                            setShowVistoria(true);
                          }}
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                    <td className="max-w-xs truncate px-6 py-4 text-sm">{row.checklist || row.notes || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Número</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Prazo defesa</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Denúncia</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Processo</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500">PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {autos.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    Nenhum auto lavrado.
                  </td>
                </tr>
              ) : (
                autos.map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-4 text-sm font-medium">{row.numero}</td>
                    <td className="px-6 py-4 text-sm">{row.tipo}</td>
                    <td className="px-6 py-4 text-sm">
                      <select
                        className="form-select py-1 text-sm"
                        value={row.status || 'lavrado'}
                        onChange={(e) => {
                          const status = e.target.value;
                          if (status === 'julgado') {
                            setJulgar({ auto: row, decisao: 'deferida', decisao_texto: '' });
                            return;
                          }
                          updateAuto.mutate({ id: row.id, status });
                        }}
                      >
                        {AUTO_STATUS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm">{row.prazo_defesa ? String(row.prazo_defesa).slice(0, 10) : '—'}</td>
                    <td className="px-6 py-4 text-sm">{complaintLabel(row.complaint_id)}</td>
                    <td className="px-6 py-4 text-sm">{processoLabel(row.processo_id)}</td>
                    <td className="px-6 py-4 text-right text-sm">
                      <button type="button" className="text-primary-700" onClick={() => downloadPdf(row)}>
                        Baixar PDF
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showVistoria && (
        <FormModalLayout
          title={editingVistoriaId ? 'Editar vistoria' : 'Nova vistoria de campo'}
          subtitle="Foto, GPS e checklist conforme LP, LI ou LO."
          size="lg"
          onClose={() => setShowVistoria(false)}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (!vistoria.complaint_id && !vistoria.license_id) {
                  toast.error('Informe a denúncia ou a licença vinculada à vistoria.');
                  return;
                }
                createVistoria.mutate({
                  id: editingVistoriaId,
                  complaint_id: vistoria.complaint_id || null,
                  license_id: vistoria.license_id || null,
                  processo_id: vistoria.processo_id || null,
                  license_tipo: vistoria.license_tipo,
                  checklist: serializeChecklist(vistoria.license_tipo, vistoria.checks),
                  notes: vistoria.notes,
                  latitude: vistoria.latitude ? Number(vistoria.latitude) : null,
                  longitude: vistoria.longitude ? Number(vistoria.longitude) : null,
                  photo_base64: vistoria.photo_base64 || null,
                  status: vistoria.status || 'aberta',
                  servidor_id: vistoria.servidor_id || null,
                });
              }}
            >
              Salvar
            </button>
          }
        >
          <div className="form-stack">
            <label className="form-label">Denúncia (obrigatória se não houver licença)</label>
            <select
              className="form-select"
              value={vistoria.complaint_id}
              onChange={(e) => setVistoria((f) => ({ ...f, complaint_id: e.target.value }))}
            >
              <option value="">Selecione a denúncia</option>
              {complaints.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.local || c.id}
                </option>
              ))}
            </select>
            <label className="form-label">Tipo de licença</label>
            <select
              className="form-select"
              value={vistoria.license_tipo}
              onChange={(e) => setVistoria((f) => ({ ...f, license_tipo: e.target.value, checks: {} }))}
            >
              <option value="LP">LP — prévia</option>
              <option value="LI">LI — instalação</option>
              <option value="LO">LO — operação</option>
            </select>
            <fieldset className="mt-2 space-y-2">
              <legend className="form-label">Checklist</legend>
              {(CHECKLISTS[vistoria.license_tipo] || []).map((label) => (
                <label key={label} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(vistoria.checks[label])}
                    onChange={(e) =>
                      setVistoria((f) => ({ ...f, checks: { ...f.checks, [label]: e.target.checked } }))
                    }
                  />
                  {label}
                </label>
              ))}
            </fieldset>
            <label className="form-label">Licença (obrigatória se não houver denúncia)</label>
            <select
              className="form-select"
              value={vistoria.license_id}
              onChange={(e) => {
                const license_id = e.target.value;
                const lic = licencas.find((l) => l.id === license_id);
                setVistoria((f) => ({
                  ...f,
                  license_id,
                  processo_id: lic?.processo_id || f.processo_id,
                }));
              }}
            >
              <option value="">Selecione a licença</option>
              {licencas.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.numero || l.id}
                </option>
              ))}
            </select>
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={vistoria.status}
              onChange={(e) => setVistoria((f) => ({ ...f, status: e.target.value }))}
            >
              {VISTORIA_STATUS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <label className="form-label">Servidor responsável</label>
            <select
              className="form-select"
              value={vistoria.servidor_id}
              onChange={(e) => setVistoria((f) => ({ ...f, servidor_id: e.target.value }))}
            >
              <option value="">Sem responsável</option>
              {(servidoresRaw || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {servidorLabel(s.id)}
                </option>
              ))}
            </select>
            <label className="form-label">Processo (necessário para lavrar auto)</label>
            <select
              className="form-select"
              value={vistoria.processo_id}
              onChange={(e) => setVistoria((f) => ({ ...f, processo_id: e.target.value }))}
            >
              <option value="">Selecione o processo</option>
              {processos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.numero || p.id}
                </option>
              ))}
            </select>
            <label className="form-label">Localização</label>
            <MapLocationPicker
              latitude={vistoria.latitude === '' ? null : Number(vistoria.latitude)}
              longitude={vistoria.longitude === '' ? null : Number(vistoria.longitude)}
              onChange={({ latitude: la, longitude: lo }) =>
                setVistoria((f) => ({ ...f, latitude: String(la), longitude: String(lo) }))
              }
              height="220px"
              defaultCenter={[-35.7353, -9.6658]}
              defaultZoom={12}
            />
            <div className="mt-2 grid grid-cols-2 gap-2">
              <input className="form-input" value={vistoria.latitude} onChange={(e) => setVistoria((f) => ({ ...f, latitude: e.target.value }))} placeholder="Latitude" />
              <input className="form-input" value={vistoria.longitude} onChange={(e) => setVistoria((f) => ({ ...f, longitude: e.target.value }))} placeholder="Longitude" />
            </div>
            <button type="button" className="btn btn-outline mt-2" onClick={captureGps}>
              Capturar GPS do aparelho
            </button>
            <label className="form-label">Foto do local</label>
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto} />
            {vistoria.photo_base64 ? (
              <img src={vistoria.photo_base64} alt="Prévia da vistoria" className="mt-2 max-h-40 rounded-lg object-cover" />
            ) : null}
            <label className="form-label">Notas</label>
            <textarea className="form-input" rows={3} value={vistoria.notes} onChange={(e) => setVistoria((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </FormModalLayout>
      )}

      {showAuto && (
        <FormModalLayout
          title="Novo auto"
          onClose={() => setShowAuto(false)}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (!auto.vistoria_id) {
                  toast.error('O auto exige uma vistoria prévia.');
                  return;
                }
                if (!auto.processo_id) {
                  toast.error('Informe o processo. A cadeia é denúncia → vistoria → auto → processo.');
                  return;
                }
                let areaGeo = auto.area_geojson?.trim() || null;
                if (areaGeo) {
                  try {
                    areaGeo = JSON.parse(areaGeo);
                  } catch {
                    toast.error('GeoJSON da área embargada inválido.');
                    return;
                  }
                }
                createAuto.mutate({
                  ...auto,
                  complaint_id: auto.complaint_id || null,
                  prazo_defesa: auto.prazo_defesa || null,
                  area_geojson: areaGeo,
                });
              }}
            >
              Salvar
            </button>
          }
        >
          <div className="form-stack">
            <label className="form-label">Tipo</label>
            <select className="form-select" value={auto.tipo} onChange={(e) => setAuto((f) => ({ ...f, tipo: e.target.value }))}>
              <option value="infracao">Infração</option>
              <option value="embargo">Embargo</option>
              <option value="notificacao">Notificação</option>
            </select>
            <label className="form-label">Status</label>
            <select className="form-select" value={auto.status} onChange={(e) => setAuto((f) => ({ ...f, status: e.target.value }))}>
              {AUTO_STATUS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <label className="form-label">Número</label>
            <input className="form-input" value={auto.numero} onChange={(e) => setAuto((f) => ({ ...f, numero: e.target.value }))} />
            <label className="form-label">Prazo de defesa</label>
            <input
              type="date"
              className="form-input"
              value={auto.prazo_defesa}
              onChange={(e) => setAuto((f) => ({ ...f, prazo_defesa: e.target.value }))}
            />
            <label className="form-label">Vistoria</label>
            <select
              className="form-select"
              value={auto.vistoria_id}
              onChange={(e) => {
                const vistoria_id = e.target.value;
                const v = vistorias.find((row) => row.id === vistoria_id);
                setAuto((f) => ({
                  ...f,
                  vistoria_id,
                  complaint_id: v?.complaint_id || f.complaint_id,
                  processo_id: v?.processo_id || f.processo_id,
                }));
              }}
            >
              <option value="">Selecione a vistoria</option>
              {vistorias.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.license_tipo || 'Campo'} · {v.id.slice(0, 8)}
                </option>
              ))}
            </select>
            <label className="form-label">Processo</label>
            <select
              className="form-select"
              value={auto.processo_id}
              onChange={(e) => setAuto((f) => ({ ...f, processo_id: e.target.value }))}
            >
              <option value="">Selecione o processo</option>
              {processos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.numero || p.id}
                </option>
              ))}
            </select>
            <label className="form-label">Denúncia</label>
            <select className="form-select" value={auto.complaint_id} onChange={(e) => setAuto((f) => ({ ...f, complaint_id: e.target.value }))}>
              <option value="">Herdada da vistoria</option>
              {complaints.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.local || c.id}
                </option>
              ))}
            </select>
            <label className="form-label">Descrição</label>
            <textarea className="form-input" rows={3} value={auto.descricao} onChange={(e) => setAuto((f) => ({ ...f, descricao: e.target.value }))} />
            {auto.tipo === 'embargo' ? (
              <>
                <label className="form-label">Polígono da área embargada (GeoJSON)</label>
                <textarea
                  className="form-input font-mono text-xs"
                  rows={4}
                  value={auto.area_geojson || ''}
                  onChange={(e) => setAuto((f) => ({ ...f, area_geojson: e.target.value }))}
                  placeholder='{"type":"Polygon","coordinates":[[[...]]]}'
                />
              </>
            ) : null}
          </div>
        </FormModalLayout>
      )}
      {julgar.auto ? (
        <FormModalLayout
          title={`Julgar auto ${julgar.auto.numero || ''}`}
          subtitle="Decisão e fundamento. Sem decisão o auto não fica julgado."
          onClose={() => setJulgar({ auto: null, decisao: 'deferida', decisao_texto: '' })}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!julgar.decisao_texto.trim() || updateAuto.isLoading}
              onClick={() =>
                updateAuto.mutate(
                  {
                    id: julgar.auto.id,
                    status: 'julgado',
                    decisao: julgar.decisao,
                    decisao_texto: julgar.decisao_texto.trim(),
                  },
                  { onSuccess: () => setJulgar({ auto: null, decisao: 'deferida', decisao_texto: '' }) }
                )
              }
            >
              Registrar decisão
            </button>
          }
        >
          <label className="form-label">Decisão</label>
          <select
            className="form-select"
            value={julgar.decisao}
            onChange={(e) => setJulgar((f) => ({ ...f, decisao: e.target.value }))}
          >
            <option value="deferida">Defesa deferida</option>
            <option value="indeferida">Defesa indeferida</option>
            <option value="parcial">Parcialmente deferida</option>
          </select>
          <label className="form-label mt-3">Fundamento</label>
          <textarea
            className="form-input"
            rows={4}
            value={julgar.decisao_texto}
            onChange={(e) => setJulgar((f) => ({ ...f, decisao_texto: e.target.value }))}
          />
          {julgar.auto.defesa_texto ? (
            <p className="mt-3 text-sm text-gray-600">Defesa: {julgar.auto.defesa_texto}</p>
          ) : null}
        </FormModalLayout>
      ) : null}
    </FormPageShell>
  );
};

export default Fiscalizacao;
